import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  GiftIcon,
  PlusCircleIcon,
  UsersIcon,
  Squares2X2Icon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { mapDualProgramLinks } from '../../utils/referralLinks';
import ClientQuickInviteCard from './ClientQuickInviteCard';
import ShortActionsPanel from './ShortActionsPanel';
import DashboardPanelHeader from './DashboardPanelHeader';
import ClientStateDistributionCard from './ClientStateDistributionCard';
import ProspectStateDistributionCard from './ProspectStateDistributionCard';
import NetworkMemberCard from './NetworkMemberCard';

function VendorHomeView({
  clients = [],
  clientsLoading = false,
  clientStateData = {},
  prospectStateData = {},
  prospectStatuses = [],
  referralLinks = [],
  pendingGifts,
  pendingGiftsLoading = false,
  hidePendingGifts = false,
  shortActions = [],
  shortActionsLoading = false,
  onRefreshShortActions,
  onCreateProspect,
  onOpenProspects,
  onOpenPendingGifts,
  onOpenClients,
  onOpenReferrals,
  onOpenAnalytics,
  onViewClient,
  getStateLabel,
}) {
  const { t, formatMessage } = useLanguage();
  const h = t.enrola.vendorHome;
  const a = t.admin;
  const v = t.vendor;

  const { clientsLink, partnersLink } = mapDualProgramLinks(referralLinks);

  const pendingCount = hidePendingGifts ? 0 : (pendingGifts?.pending_count ?? 0);
  const awaitingAdminCount = hidePendingGifts ? 0 : (pendingGifts?.awaiting_admin_count ?? 0);
  const totalPending = pendingCount + awaitingAdminCount;

  const recentClients = [...clients]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, 10);

  const inviteLabels = {
    inviteEyebrow: h.inviteEyebrow,
    inviteTitle: h.inviteTitle,
    inviteSubtitle: h.inviteSubtitle,
    scanToJoin: h.scanToJoin,
    openReferrals: h.openReferrals,
    noInviteLink: h.noInviteLink,
  };

  const partnerLabels = t.enrola.partnerHome;

  const shortActionRunnerRef = useRef(null);

  return (
    <div className="space-y-6 xl:space-y-7">
      <ShortActionsPanel
        actions={shortActions}
        loading={shortActionsLoading}
        role="vendor"
        layout="banner"
        onRefresh={onRefreshShortActions}
        onNavigateProspects={onOpenProspects}
        onNavigateClients={onOpenClients}
        onNavigatePendingGifts={onOpenPendingGifts}
        actionRunnerRef={shortActionRunnerRef}
      />

      {/* ROW 1 — Referral QR + Direct access, shared row like the client dashboard */}
      <div className="grid gap-5 xl:gap-6 lg:grid-cols-2 lg:items-stretch">
        <section className="min-w-0">
          <ClientQuickInviteCard
            link={clientsLink}
            secondaryLink={partnersLink}
            onOpenReferrals={onOpenReferrals}
            labels={inviteLabels}
            secondaryLabels={partnerLabels}
            layout="vertical"
            compact
            qrSize={240}
            className="h-full"
          />
        </section>

        <section className="min-w-0 space-y-4 lg:flex lg:h-full lg:flex-col xl:space-y-5">
          <DashboardPanelHeader
            eyebrow={h.shortcutsEyebrow}
            title={h.shortcutsTitle}
            subtitle={h.shortcutsSubtitle}
            icon={Squares2X2Icon}
            className="shrink-0"
          />

          <div className="grid gap-4 lg:min-h-0 lg:flex-1 lg:auto-rows-fr">
          <motion.button
            type="button"
            whileTap={{ scale: 0.995 }}
            onClick={onCreateProspect}
            className="card-premium card-premium-accent interactive-lift group flex flex-col justify-between bg-gradient-to-br from-orange-500 to-amber-500 p-5 text-left"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-white/80">{h.createProspectEyebrow}</p>
                <h4 className="mt-1 text-lg font-bold">{h.createProspectTitle}</h4>
                <p className="mt-1 text-sm text-white/90">{h.createProspectSubtitle}</p>
              </div>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30">
                <PlusCircleIcon className="h-7 w-7" />
              </div>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
              {h.createProspectCta}
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </motion.button>

          {!hidePendingGifts ? (
          <motion.button
            type="button"
            whileTap={{ scale: 0.995 }}
            onClick={onOpenPendingGifts}
            className="card-premium interactive-lift group relative flex flex-col justify-between p-5 text-left"
          >
            {totalPending > 0 ? (
              <span className="absolute right-4 top-4 flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-orange-500 px-2 text-xs font-bold text-white">
                {totalPending}
              </span>
            ) : null}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 ">
                  {h.pendingGiftsEyebrow}
                </p>
                <h4 className="mt-1 text-lg font-bold text-secondary-900 ">
                  {h.pendingGiftsTitle}
                </h4>
                <p className="mt-1 text-sm text-secondary-600 ">
                  {pendingGiftsLoading
                    ? '…'
                    : totalPending > 0
                      ? formatMessage(h.pendingGiftsCount, { count: pendingCount, awaiting: awaitingAdminCount })
                      : h.pendingGiftsNone}
                </p>
              </div>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600  ">
                <GiftIcon className="h-7 w-7" />
              </div>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 ">
              {h.pendingGiftsCta}
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </motion.button>
          ) : (
          <motion.button
            type="button"
            whileTap={{ scale: 0.995 }}
            onClick={onOpenClients}
            className="card-premium interactive-lift group flex flex-col justify-between p-5 text-left"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500 ">
                  {h.clientsEyebrow}
                </p>
                <h4 className="mt-1 text-lg font-bold text-secondary-900 ">
                  {h.clientsTitle}
                </h4>
                <p className="mt-1 text-sm text-secondary-600 ">{h.clientsSubtitle}</p>
              </div>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600  ">
                <UsersIcon className="h-7 w-7" />
              </div>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 ">
              {h.clientsCta}
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </motion.button>
          )}
        </div>
        </section>
      </div>

      {/* ROW 2 — This month / Your performance */}
      <section className="min-w-0 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="micro text-orange-600 ">{h.performanceEyebrow}</p>
            <h3 className="text-base font-bold text-secondary-900  sm:text-lg">
              {h.performanceTitle}
            </h3>
          </div>
          {onOpenAnalytics ? (
            <button
              type="button"
              onClick={onOpenAnalytics}
              className="touch-target flex shrink-0 items-center gap-2 rounded-xl border border-secondary-200/80 bg-white/90 px-3 py-2 text-xs font-semibold text-secondary-600 transition-colors hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600       lg:px-2.5 lg:py-2"
              aria-label={h.viewAnalytics}
              title={h.viewAnalytics}
            >
              <ChartBarIcon className="h-5 w-5 shrink-0" />
              <span>{h.viewAnalytics}</span>
            </button>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="min-w-0">
            <p className="mb-2 text-xs font-medium text-secondary-500 ">
              {v.clientStateDistribution}
            </p>
            <ClientStateDistributionCard
              carousel
              stateData={clientStateData}
              labels={{
                affiliated: a.affiliated,
                contacted: a.contacted,
                visited: a.visited,
                completed: a.completed,
              }}
            />
          </div>
          <div className="min-w-0">
            <p className="mb-2 text-xs font-medium text-secondary-500 ">
              {v.prospectStateDistribution}
            </p>
            <ProspectStateDistributionCard
              carousel
              stateData={prospectStateData}
              prospectStatuses={prospectStatuses}
              t={t}
              delay={0.05}
            />
          </div>
        </div>
      </section>

      {/* ROW 3 — Pipeline / Recent clients */}
      <section className="min-w-0 border-t border-secondary-100 pt-5 ">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="micro text-secondary-500 ">
              {h.newClientsEyebrow}
            </p>
            <h4 className="text-base font-bold text-secondary-900  sm:text-lg">
              {h.newClientsTitle}
            </h4>
          </div>
          {onOpenClients && recentClients.length > 0 ? (
            <button
              type="button"
              onClick={onOpenClients}
              className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700 "
            >
              {h.viewAllClients}
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {clientsLoading ? (
          <div className="flex gap-3 overflow-x-auto pb-1" aria-busy="true" aria-live="polite">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`client-skeleton-${index}`}
                className="h-[88px] min-w-[172px] shrink-0 animate-pulse rounded-2xl bg-secondary-100 "
              />
            ))}
          </div>
        ) : recentClients.length > 0 ? (
          <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:thin]">
            {recentClients.map((client) => {
              const stateInfo = getStateLabel?.(client.state);
              return (
                <NetworkMemberCard
                  key={client.id}
                  name={client.username}
                  avatarUrl={client.avatar_url}
                  joinedLabel={
                    client.created_at
                      ? new Date(client.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                      : ''
                  }
                  stateLabel={stateInfo?.label || client.state}
                  referralsLabel={formatMessage(t.enrola.referralsCount, { count: client.referrals_count || 0 })}
                  onClick={onViewClient ? () => onViewClient(client) : undefined}
                />
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-secondary-200 bg-secondary-50/50 px-4 py-5 text-center  ">
            <UsersIcon className="mx-auto mb-2 h-6 w-6 text-secondary-400" />
            <p className="text-sm text-secondary-600 ">{h.noClientsYet}</p>
            {onCreateProspect ? (
              <button
                type="button"
                onClick={onCreateProspect}
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-orange-600 hover:text-orange-700 "
              >
                {h.createProspectCta}
                <ArrowTrendingUpIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}

export default VendorHomeView;
